import React from 'react';
import produce from 'immer';
import queryString from 'query-string';
import classNames from 'classnames';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { Link } from 'react-router-dom';

import { trimDialCode, validate } from './validator';
import type { IProps, IState } from './types';

import PrivacyPolicy from '../privacy-policy';
import Input from '../../../../shared/design-system/components/input';
import Button from '../../../../shared/design-system/ui/button';
import PasswordValidationChecklist from '../../../../shared/components/password-validation-checklist';
import { SHLogoDark } from '../../../../shared/svg';

import {
  getCurrentTimeZone,
  getIsRequestPending,
  redirectWithoutRefresh,
  initializeThirdPartyIntegrations,
  executeOnRequestStatusWithPrevStatusCheck,
  AuthHelper,
  setToken,
  getCookie,
} from '../../../../shared/utils';
import { isWhiteLabel } from '../../../../shared/utils/user-details';
import { pushSignupEvent } from '../../../../shared/utils/gtm-events';
import OAuthButtons from '../oauth-buttons';
import { ActionForAuthentication } from '../../types';

class SignupForm extends React.PureComponent<IProps, IState> {
  private readonly phoneInputField = React.createRef<HTMLInputElement>();

  private utmSource = '';

  private isUnmounted = false;

  constructor(props: IProps) {
    super(props);

    const { location, email: propsEmail, isInvitedUser } = this.props;
    const { email: queryEmail } = queryString.parse(location.search);
    const disableEmailInputValue = isInvitedUser || queryEmail;

    this.state = {
      values: {
        firstName: '',
        lastName: '',
        email: propsEmail || queryEmail || '',
        password: '',
        phone: '',
      },
      errors: {
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        phone: '',
      },
      dirty: {
        firstName: false,
        lastName: false,
        email: disableEmailInputValue || false,
        password: false,
        phone: true,
      },
      disableEmailInput: disableEmailInputValue,
      dialCode: '1',
      showPassword: false,
      shouldShowPhoneNumberInput: true,
      phoneCountry: 'us',
    };

    this.onInputChange = this.onInputChange.bind(this);
    this.onInputBlur = this.onInputBlur.bind(this);
    this.onPhoneInputChange = this.onPhoneInputChange.bind(this);
    this.onPasswordVisibilityChange = this.onPasswordVisibilityChange.bind(
      this,
    );
    this.onFormSubmit = this.onFormSubmit.bind(this);
    this.handleSuccess = this.handleSuccess.bind(this);
    this.hasErrors = this.hasErrors.bind(this);
  }

  componentDidMount(): void {
    const { location } = this.props;
    const { search } = location;
    const searchParams = new URLSearchParams(search);

    const utmSource = searchParams?.get('utm_source') || '';

    if (utmSource) {
      this.utmSource = utmSource;
    }

    this.setPhoneCountryFromGeoIp();
  }

  componentDidUpdate(prevProps: Readonly<IProps>) {
    const {
      signupRequestStatus,
      acceptInvitationRequestStatus,
      trackingId,
    } = this.props;
    const { values } = this.state;

    executeOnRequestStatusWithPrevStatusCheck({
      status: signupRequestStatus,
      prevStatus: prevProps.signupRequestStatus,
      onSuccess: () => {
        pushSignupEvent('email');
        this.handleSuccess();

        const { firstName, lastName, email } = values;

        initializeThirdPartyIntegrations({
          trackingId,
          firstName,
          lastName,
          email,
        });
      },
    });

    executeOnRequestStatusWithPrevStatusCheck({
      status: acceptInvitationRequestStatus,
      prevStatus: prevProps.acceptInvitationRequestStatus,
      onSuccess: () => {
        pushSignupEvent('email');
        this.handleSuccess();

        const { firstName, lastName, email } = values;

        initializeThirdPartyIntegrations({
          trackingId,
          firstName,
          lastName,
          email,
        });
      },
    });
  }

  componentWillUnmount() {
    const { hideError } = this.props;
    hideError?.();
    this.isUnmounted = true;
  }

  onInputChange(value: string, e: React.ChangeEvent<HTMLInputElement>) {
    const { name } = e.target;
    const { dialCode, shouldShowPhoneNumberInput } = this.state;

    this.setState((prevState) => ({
      dirty: {
        ...prevState.dirty,
        [name]: true,
      },
      values: {
        ...prevState.values,
        [name]: value,
      },
      errors: {
        ...prevState.errors,
        [name]: '',
      },
    }));

    if (name === 'password') {
      const error = validate(name, value, dialCode, shouldShowPhoneNumberInput);
      this.setState((prevState) => ({
        errors: {
          ...prevState.errors,
          [name]: error,
        },
      }));
    }
  }

  onInputBlur(e) {
    if (e?.target) {
      const { name } = e.target;
      const { dialCode, values, shouldShowPhoneNumberInput } = this.state;

      this.setState((prevState) => ({
        errors: {
          ...prevState.errors,
          [name]: validate(
            name,
            values[name],
            dialCode,
            shouldShowPhoneNumberInput,
          ),
        },
      }));
    }
  }

  onPhoneInputChange(value: string, data) {
    const { dialCode, errors, shouldShowPhoneNumberInput } = this.state;

    if (data && dialCode !== data.dialCode) {
      this.setState({ dialCode: data.dialCode });
    }

    if (
      errors.phone &&
      validate('phone', value, dialCode, shouldShowPhoneNumberInput) === ''
    ) {
      this.setState((prevState) => ({
        errors: {
          ...prevState.errors,
          phone: '',
        },
      }));
    }

    this.setState(
      produce((draft) => {
        draft.values.phone = value;
        draft.dirty.phone = true;
      }),
    );
  }

  onPasswordVisibilityChange() {
    const { showPassword } = this.state;

    this.setState({ showPassword: !showPassword });
  }

  onFormSubmit(e) {
    e.preventDefault();

    const {
      dirty,
      errors,
      values,
      dialCode,
      shouldShowPhoneNumberInput,
    } = this.state;
    const { location } = this.props;
    const celloReferralCode = window?.CelloAttribution?.('getReferral') || '';
    const referralId = celloReferralCode ? null : (window?.tolt_referral ?? null);

    const dirtyRef = { ...dirty };
    const dirtyKeys = Object.keys(dirtyRef);

    dirtyKeys.forEach((key) => {
      dirtyRef[key] = true;
    });

    const errorsRef = { ...errors };
    const errorsKeys = Object.keys(errorsRef);
    let isError = false;

    errorsKeys.forEach((key) => {
      const error = validate(
        key,
        values[key],
        dialCode,
        shouldShowPhoneNumberInput,
      );

      errorsRef[key] = error;
      isError = isError || !!error;
    });

    this.setState({ errors: errorsRef, dirty: dirtyRef });

    if (isError) {
      return;
    }

    const timeZone = getCurrentTimeZone();

    const {
      sendSignupRequest,
      sendAcceptInvitationRequest,
      isInvitedUser,
      invitationToken,
    } = this.props;

    if (isInvitedUser) {
      sendAcceptInvitationRequest({
        firstName: values.firstName,
        lastName: values.lastName,
        password: values.password,
        ...(trimDialCode(values.phone, dialCode) !== '' &&
          shouldShowPhoneNumberInput && { phone: values.phone }),
        token: invitationToken,
        timeZone,
      });
    } else {
      sendSignupRequest({
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        password: values.password,
        ...(trimDialCode(values.phone, dialCode) !== '' &&
          shouldShowPhoneNumberInput && { phone: values.phone }),
        timeZone,
        rewardfulReferralId: referralId,
        landingPage: getCookie('sh_ulp') || '',
        celloReferralCode,
        ...(this?.utmSource ? { utmSource: this.utmSource } : {}),
      });
      
      if (referralId) {
        window.tolt?.signup(values.email);
      }
    }
  }

  async setPhoneCountryFromGeoIp() {
    try {
      const response = await fetch('https://get.geojs.io/v1/ip/country.json');
      const data = await response.json();

      const rawCountry = data?.country || data?.country_code || '';

      const country = rawCountry?.trim()?.toLowerCase();

      if (!this.isUnmounted && country?.length === 2) {
        this.setState({ phoneCountry: country });
      }
    } catch (error) {
      console.warn('failed to fetch user country code');
    }
  }

  handleSuccess() {
    const { token } = this.props;
    const isUtmSourceGrowthHub = this?.utmSource === 'growth-hub';

    if (token) {
      setToken(token);
    }

    // If user came from Growth Hub, skip onboarding and redirect to growth hub
    if (isUtmSourceGrowthHub) {
      AuthHelper.login({
        token,
        redirectUrl: '/growth-hub',
      });
    } else {
      AuthHelper.login({ token });
      redirectWithoutRefresh({
        pathname: '/sequence',
        search: 'signup=completed',
      });
    }
  }

  hasErrors() {
    const { errors, dirty, values } = this.state;

    let isError = false;

    Object.keys(errors).forEach((key) => {
      if (
        (errors[key] !== '' || (!dirty[key] && key !== 'password')) &&
        key !== 'password'
      ) {
        isError = true;
      }
    });

    if (
      values.password.length === 0 ||
      errors.password !== '' ||
      !dirty.password
    ) {
      isError = true;
    }

    return isError;
  }

  render() {
    const {
      values,
      errors,
      dirty,
      showPassword,
      disableEmailInput,
      shouldShowPhoneNumberInput,
      phoneCountry,
    } = this.state;
    const {
      signupRequestStatus,
      signupRequestError,
      showSignupRequestError,
      isInvitedUser = false,
      email: propsEmail,
    } = this.props;
    const isSignupBtnLoading = getIsRequestPending(signupRequestStatus);
    const isSignupBtnDisabled = isSignupBtnLoading || this.hasErrors();

    return (
      <div
        className={
          isWhiteLabel() ? 'auth-wrapper w-100 d-flex' : 'auth-wrapper'
        }
      >
        <div className="auth-container">
          <div className="auth-form">
            <div className="sh-logo">
              <SHLogoDark width={200} />
            </div>
            <h1 className="auth-container--title">
              {isInvitedUser ? (
                <>
                  <span>Let's go!</span>
                  <span>Your team is waiting for you.</span>
                </>
              ) : (
                <span>Create your account</span>
              )}
            </h1>

            <OAuthButtons
              type={ActionForAuthentication.Signup}
              email={propsEmail}
              {...(this?.utmSource ? { utmSource: this.utmSource } : {})}
            />

            <form onSubmit={this.onFormSubmit} className="auth-container--form">
              <div className="auth-form-row">
                <div className="auth-form-input">
                  <Input
                    name="firstName"
                    label="First name*"
                    placeholder="John"
                    value={values.firstName}
                    variant={errors.firstName && Input.Variant.Error}
                    caption={errors.firstName}
                    onChange={this.onInputChange}
                    onBlur={this.onInputBlur}
                    tabIndex={0}
                    autoFocus
                    autoComplete="current-firstName"
                  />
                </div>

                <div className="auth-form-input">
                  <Input
                    name="lastName"
                    label="Last name*"
                    placeholder="Doe"
                    value={values.lastName}
                    variant={errors.lastName && Input.Variant.Error}
                    caption={errors.lastName}
                    onChange={this.onInputChange}
                    onBlur={this.onInputBlur}
                    tabIndex={0}
                    autoComplete="current-lastName"
                  />
                </div>
              </div>

              <div className="auth-form-row">
                <div
                  className={classNames([
                    'auth-form-input',
                    { 'email-container': !shouldShowPhoneNumberInput },
                  ])}
                >
                  <Input
                    name="email"
                    label="Work email*"
                    type="email"
                    placeholder="johndoe@example.com"
                    value={values.email}
                    variant={errors.email && Input.Variant.Error}
                    caption={
                      errors.email ||
                      (showSignupRequestError ? signupRequestError.message : '')
                    }
                    onChange={this.onInputChange}
                    onBlur={this.onInputBlur}
                    disabled={disableEmailInput}
                    tabIndex={0}
                    autoComplete="current-email"
                  />
                </div>
                {shouldShowPhoneNumberInput && !isInvitedUser && (
                  <div className="auth-form-input phone">
                    <p>Phone number</p>
                    <PhoneInput
                      countryCodeEditable={false}
                      country={phoneCountry}
                      value={values.phone}
                      onChange={(value, data) =>
                        this.onPhoneInputChange(value, data)
                      }
                      onBlur={this.onInputBlur}
                      containerClass={classNames([
                        'phone-form-input',
                        { 'input-error': errors.phone },
                      ])}
                      inputProps={{
                        tabIndex: 0,
                        name: 'phone',
                        ref: this.phoneInputField,
                      }}
                    />
                    <span className="phone-form-input-error">
                      {errors.phone}
                    </span>
                  </div>
                )}
              </div>

              <div className="auth-form-row">
                <div className="auth-form-input password">
                  <Input
                    name="password"
                    label="Password*"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Minimum 8 Characters"
                    value={values.password}
                    variant={errors.password && Input.Variant.Error}
                    onChange={this.onInputChange}
                    onBlur={this.onInputBlur}
                    className="auth-form-input"
                    autoComplete="current-password"
                    icons={[
                      {
                        place: Input.IconPlace.Right,
                        identifier: showPassword ? 'eye-alt' : 'eye',
                        className: 'pointer',
                        onClick: this.onPasswordVisibilityChange,
                      },
                    ]}
                  />
                  {isSignupBtnDisabled ? (
                    <PasswordValidationChecklist
                      password={values.password}
                      isDirty={dirty.password}
                    />
                  ) : (
                    <span className="regular-1 font-medium green-txt-16 mt-1">
                      Your password is secured & You're all set!
                    </span>
                  )}
                </div>
              </div>

              <Button
                type="submit"
                isFullWidth
                isLoading={isSignupBtnLoading}
                disabled={isSignupBtnDisabled}
                loadingText="Signing up, please wait..."
              >
                Sign up
              </Button>
            </form>
          </div>

          <div className="d-flex flex-column align-items-center text-center">
            {!isWhiteLabel() && <PrivacyPolicy />}

            <div className="bottom-navigation">
              <p>
                Already have an account?{' '}
                <Link
                  to={
                    this?.utmSource
                      ? `/login?utm_source=${this.utmSource}`
                      : '/login'
                  }
                  tabIndex={0}
                >
                  Log in!
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default SignupForm;
